/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectTargetDate,
  selectVisualFilters,
  selectHotelName,
  selectEnableHotelGroups,
  selectPmsSync,
  selectReportType,
  selectIsDemoUser,
  selectLocalCurrency,
  selectWorkbookDateType,
  selectIsPrimaryHotelSetOverTime,
  selectInvalidPrimaryHotelList,
  selectResetBookingDate,
  selectPartialHours,
  selectLastPartialBookingDate,
} from 'modules/dashboard/selectors';
import {
  generateWorkbookShareUrl,
  getAlignmentAndDateComparisonIndexSchema,
  getBookingDateSchema,
  getComparisonBookingDateSchema,
  getComparisonStayDateSchema,
  getDashboardPmsSyncSchema,
  getDateSwitchSchema,
  getHotelGroupASchema,
  getHotelGroupOrderSchema,
  getHotelGroupSchema,
  getHotelIdSchema,
  getMaxStayDateSchema,
  getNormalizedViewSchema,
  getPmsSyncSchema,
  getSymmetricComparisonSchema,
  getWorkbookTargetDateSchema,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { workbookActions } from 'modules/dashboard/components/tab-container/workbook-report/slice';
import {
  selectAlignment,
  selectDateComparison,
  selectWorkbookBookingDate,
  selectSymmetricComparisonToggle,
  selectWorkbookEndDate,
  selectWorkbookStartDate,
  selectWorkbookOtb,
  selectWorkbookAlignmentToggle,
  selectWorkbookCustomDate,
  selectD1SetIsDisplay,
  selectD2SetIsDisplay,
  selectD3SetIsDisplay,
  selectWorkbookFilterList,
  selectEnableSecondaryHotelGroups,
  selectHotelFocusOn,
  selectNormalizedView,
  selectWorkbookHotelComparison,
  selectWorkbookHotelFilterList,
  selectRatePlanFilterList,
  selectIsRatePlan,
  selectSymmetricComparisonIsDisplay,
  selectIsDetailed,
  selectIsTravelAgent,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
  selectIsRoomNightsPace,
  selectIsRoomType
} from 'modules/dashboard/components/tab-container/workbook-report/selectors';
import { REPORT_TYPE, TAGS } from 'modules/dashboard/constants';
import writeLog from 'modules/common/utils/filter-log';
import getMapperResult from '../function';
import calculateComparisonDates, {
  hasLeapDay,
} from '../../../../../common/utils/dateComparisonUtils';
/**
 * Custom hook to generate summary widget filter schemas
 */
const useWorkbookWidget = () => {
  const dispatch = useDispatch();
  //
  const alignment = useSelector(selectAlignment);
  const dateComparison = useSelector(selectDateComparison);
  const bookingDate = useSelector(selectWorkbookBookingDate);
  const targetDate = useSelector(selectTargetDate);
  const hotelId = useSelector(selectHotelName);
  const visualFilters = useSelector(selectVisualFilters);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const endDate = useSelector(selectWorkbookEndDate);
  const startDate = useSelector(selectWorkbookStartDate);
  const customBooking = useSelector(selectWorkbookOtb);
  const alignmentToggle = useSelector(selectWorkbookAlignmentToggle);
  const customDate = useSelector(selectWorkbookCustomDate);
  const symmetricComparisonIsDisplay = useSelector(selectSymmetricComparisonIsDisplay);
  const d1SetIsDisplay = useSelector(selectD1SetIsDisplay);
  const d2SetIsDisplay = useSelector(selectD2SetIsDisplay);
  const d3SetIsDisplay = useSelector(selectD3SetIsDisplay);
  const reportType = useSelector(selectReportType);
  const workbookFilterList = useSelector(selectWorkbookFilterList);
  const workbookHotelFilterList = useSelector(selectWorkbookHotelFilterList);
  const isDemoUser = useSelector(selectIsDemoUser);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const isWorkbookHotelComparison = useSelector(selectWorkbookHotelComparison);
  const localCurrency = useSelector(selectLocalCurrency);
  const selectedDateType = useSelector(selectWorkbookDateType);
  const ratePlanFilterList = useSelector(selectRatePlanFilterList);
  const isRatePlan = useSelector(selectIsRatePlan);
  const isDetailed = useSelector(selectIsDetailed);
  const isTravelAgentOn = useSelector(selectIsTravelAgent);
  const isPrimaryHotelSetOverTime = useSelector(selectIsPrimaryHotelSetOverTime);
  const invalidPrimaryHotels = useSelector(selectInvalidPrimaryHotelList);
  const isSecondaryHotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const invalidSecondaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const isRoomNightsPace = useSelector(selectIsRoomNightsPace);
  const isRoomType = useSelector(selectIsRoomType)
  const resetBookingDate = useSelector(selectResetBookingDate);
  const partialHours = useSelector(selectPartialHours);
  const lastPartialBookingDate = useSelector(selectLastPartialBookingDate);

  // Triggered when bookingDate,visualFilters,dateComparison,alignment,targetDate,hotelId,symmetricComparisonToggle value changes
  useEffect(() => {
    if (
      reportType !== REPORT_TYPE.WORKBOOK_REPORT ||
      !hotelId ||
      (!dateComparison && !alignment && !bookingDate)
    )
      return;
    // Generate filter list for summary widget top 8 visuals
    const targetDateFilter = getWorkbookTargetDateSchema(
      visualFilters,
      selectedDateType?.id,
      targetDate?.startDate,
      targetDate?.endDate
    );
    const dateSwitchFilter = getDateSwitchSchema(visualFilters, selectedDateType?.id);
    //
    let hotelListIds = [];
    let hotelGroup1Filter;
    let primaryHotelIdFilter;

    // Extract invalid primary IDs
    const invalidPrimaryIds = invalidPrimaryHotels.map((hotel) => hotel.hotelCode);

    if (hotelId?.hotels) {
      if (isPrimaryHotelSetOverTime) {
        hotelListIds = hotelId.hotels
          .filter((hotel) => !invalidPrimaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        hotelListIds = hotelId?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }
    // Extract invalid secondary IDs
    const invalidSecondaryIds = invalidSecondaryHotels.map((hotel) => hotel.hotelCode);

    let groupFocusOnIds = [];
    if (hotelFocusOn?.hotels) {
      if (isSecondaryHotelSetOverTime) {
        groupFocusOnIds = hotelFocusOn.hotels
          .filter((hotel) => !invalidSecondaryIds.includes(hotel?.hotelCode))
          .map((hotel) => hotel?.hotelCode);
      } else {
        groupFocusOnIds = hotelFocusOn?.hotels?.map((hotel) => hotel?.hotelCode) ?? [];
      }
    }
    //
    if (enableHotelGroups) {
      hotelGroup1Filter = getHotelGroupSchema(hotelListIds);
      primaryHotelIdFilter = getHotelIdSchema(hotelListIds, visualFilters);
    } else {
      hotelGroup1Filter = getHotelGroupSchema([hotelId?.hotelCode]);
      primaryHotelIdFilter = getHotelIdSchema([hotelId?.hotelCode], visualFilters);
    }
    //
    let hotelGroupAFilter;
    if (enableSecondaryHotelGroups) {
      hotelGroupAFilter = getHotelGroupASchema(groupFocusOnIds);
    } else {
      hotelGroupAFilter = getHotelGroupASchema([hotelFocusOn?.hotelCode]);
    }
    //
    let dimHotelIds = [];
    if (enableHotelGroups) {
      if (enableSecondaryHotelGroups) {
        dimHotelIds = new Set([...groupFocusOnIds, ...hotelListIds]);
      } else {
        dimHotelIds = new Set([hotelFocusOn?.hotelCode, ...hotelListIds]);
      }
    } else if (enableSecondaryHotelGroups) {
      dimHotelIds = new Set([...groupFocusOnIds, hotelId?.hotelCode]);
    } else {
      dimHotelIds = new Set([hotelFocusOn?.hotelCode, hotelId?.hotelCode]);
    }
    //
    const secondaryHotelIdFilter = getHotelIdSchema(Array.from(dimHotelIds), visualFilters);
    const hotelFilters = isWorkbookHotelComparison
      ? [secondaryHotelIdFilter, hotelGroupAFilter, hotelGroup1Filter]
      : [secondaryHotelIdFilter];
    const normalizedFilter = getNormalizedViewSchema(visualFilters, normalizedViewToggle);
    const pmsSyncFilter = getPmsSyncSchema(visualFilters, pmsSyncToggle, TAGS.SUMMARY);
    const dashboardPmsSyncSchema = getDashboardPmsSyncSchema(pmsSyncToggle);
    const bookingDateFilter = getBookingDateSchema(
      visualFilters,
      bookingDate?.startDate,
      bookingDate?.endDate
    );
    const alignmentAndDateComparisonFilter = getAlignmentAndDateComparisonIndexSchema(
      visualFilters,
      alignment?.id,
      getMapperResult(alignmentToggle, dateComparison)
    );
    const symmetricComparisonFilter = getSymmetricComparisonSchema(
      visualFilters,
      symmetricComparisonToggle
    );
    const { adjustedBookingDate, adjustedStayDate } = calculateComparisonDates(
      targetDate,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      { startDate, endDate },
      resetBookingDate,
      partialHours,
      lastPartialBookingDate
    );
    const comparisonBookingDateFilter = getComparisonBookingDateSchema(
      visualFilters,
      adjustedBookingDate?.startDate,
      adjustedBookingDate?.endDate
    );
    const comparisonStayDateFilter = getComparisonStayDateSchema(
      visualFilters,
      adjustedStayDate?.startDate,
      adjustedStayDate?.endDate
    );
    const maxStayDateFilter = getMaxStayDateSchema(visualFilters, targetDate?.endDate);
    const hotelGroupOrderFilter = getHotelGroupOrderSchema(visualFilters, isDemoUser);
    //
    const hasLeap = hasLeapDay(adjustedStayDate?.startDate, adjustedStayDate?.endDate);
    dispatch(workbookActions.setIsDashboardLeapSelection(hasLeap));
    //
    if (
      pmsSyncFilter &&
      dashboardPmsSyncSchema &&
      bookingDateFilter &&
      alignmentAndDateComparisonFilter &&
      targetDateFilter &&
      hotelFilters &&
      symmetricComparisonFilter &&
      hotelGroupOrderFilter &&
      normalizedFilter &&
      dateSwitchFilter &&
      primaryHotelIdFilter
    ) {
      //  IMPORTANT :  Keep comparisonBookingDateFilter,comparisonStayDateFilter, maxStayDateFilter filter order in filters list as it is (last 3 filters)
      //  since that order is used in adding PDF generation dynamic filters
      const filters = [
        ...pmsSyncFilter,
        ...dashboardPmsSyncSchema,
        bookingDateFilter,
        targetDateFilter,
        secondaryHotelIdFilter,
        alignmentAndDateComparisonFilter,
        symmetricComparisonFilter,
        dateSwitchFilter,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      const rateFilters = [
        ...pmsSyncFilter,
        ...dashboardPmsSyncSchema,
        bookingDateFilter,
        targetDateFilter,
        alignmentAndDateComparisonFilter,
        symmetricComparisonFilter,
        dateSwitchFilter,
        primaryHotelIdFilter,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];

      const hotelComparisonFilters = [
        ...pmsSyncFilter,
        ...dashboardPmsSyncSchema,
        bookingDateFilter,
        targetDateFilter,
        secondaryHotelIdFilter,
        hotelGroupAFilter,
        hotelGroup1Filter,
        hotelGroupOrderFilter,
        normalizedFilter,
        dateSwitchFilter,
        comparisonBookingDateFilter,
        comparisonStayDateFilter,
        maxStayDateFilter,
      ];
      if (JSON.stringify(filters) !== JSON.stringify(workbookFilterList)) {
        dispatch(workbookActions.updateWorkbookFilterList(filters));
        writeLog('Dashboard Visual Filters', filters);
      }
      if (JSON.stringify(rateFilters) !== JSON.stringify(ratePlanFilterList)) {
        dispatch(workbookActions.updateRatePlanFilterList(rateFilters));
        writeLog('Rate Plan Visual Filters', rateFilters);
      }
      if (JSON.stringify(hotelComparisonFilters) !== JSON.stringify(workbookHotelFilterList)) {
        writeLog('Dashboard Visual Filters', hotelComparisonFilters);
      }
      dispatch(workbookActions.updateWorkbookHotelFilterList(hotelComparisonFilters));
    }
  }, [
    bookingDate,
    pmsSyncToggle,
    visualFilters,
    dateComparison,
    alignment,
    targetDate,
    hotelId,
    symmetricComparisonToggle,
    enableHotelGroups,
    reportType,
    startDate,
    endDate,
    isDemoUser,
    enableSecondaryHotelGroups,
    hotelFocusOn,
    normalizedViewToggle,
    isWorkbookHotelComparison,
    selectedDateType,
    isPrimaryHotelSetOverTime,
    invalidPrimaryHotels,
    isSecondaryHotelSetOverTime,
    invalidSecondaryHotels,
    resetBookingDate,
  ]);

  // Triggers when filter values dependencies changes
  useEffect(() => {
    if (!dateComparison && !bookingDate && !targetDate && !hotelId) return;
    // Generate workbook filter url
    const workbookUrl = generateWorkbookShareUrl(
      pmsSyncToggle,
      hotelId,
      targetDate,
      dateComparison,
      startDate,
      endDate,
      customDate,
      customBooking,
      bookingDate,
      alignmentToggle,
      symmetricComparisonToggle,
      enableHotelGroups,
      symmetricComparisonIsDisplay,
      d1SetIsDisplay,
      d2SetIsDisplay,
      d3SetIsDisplay,
      enableSecondaryHotelGroups,
      hotelFocusOn,
      normalizedViewToggle,
      localCurrency,
      selectedDateType,
      isRatePlan,
      isTravelAgentOn,
      isDetailed,
      isRoomNightsPace,
      isRoomType
    );
    //
    dispatch(workbookActions.setWorkbookUrl(workbookUrl));
  }, [
    pmsSyncToggle,
    hotelId,
    targetDate,
    dateComparison,
    startDate,
    endDate,
    customDate,
    customBooking,
    bookingDate,
    alignmentToggle,
    symmetricComparisonToggle,
    enableHotelGroups,
    symmetricComparisonIsDisplay,
    d1SetIsDisplay,
    d2SetIsDisplay,
    d3SetIsDisplay,
    enableSecondaryHotelGroups,
    hotelFocusOn,
    normalizedViewToggle,
    localCurrency,
    selectedDateType,
    isRatePlan,
    isTravelAgentOn,
    isDetailed,
    isRoomNightsPace,
    isRoomType
  ]);
};
//
export default useWorkbookWidget;
